import React, { useCallback } from 'react';
import {
    Box,
    Button,
    Checkbox,
    lighten,
    Table,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { alertErrorIcon, FaOptionIcon } from '@europrocurement/l2d-icons';
import {
    ContextualMenu,
    ContextualMenuItemProps,
    InformationTableRow,
    TextIcon,
    useModal,
} from '@europrocurement/flexy-components';
import {
    SelectGreffeModal,
    REFRESH_CURRENT_DOSSIER_CTX,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
    useDossierDocumentsCtx,
    useDossierProcesses,
    useDossierProcessesActionsAndRender,
    useFormalitiesCtx,
} from '@europrocurement/l2d-modules';
import { enqueueSnackbar } from 'notistack';
import DossierArticleProcessStatus from '../DossierArticleProcessStatus/DossierArticleProcessStatus';

type DossierFormaliteProcessListRowProps = {
    libelle: string;
    documentsCount: number;
    complete: boolean;
    optionsCount: number;
    externalUrl?: string;
    onExternalUrlClick: (externalUrl: string) => void;
    id: number;
    prestationId: number[];
    greffes?: Array<{ id?: number; libelle?: string }>;
};

type ListRowProcess = {
    validatorId: number;
    validatorLabel: string;
    identifier?: string;
    statusLabel: string;
    statusFlag: number;
    statusStyle: string;
    onValidatorClick?: () => void;
    onStatusClick?: () => void;
};

type DossierFormaliteProcessListRowsProps = {
    formalities: DossierFormaliteProcessListRowProps[];
    selectedFormalitiesIds: number[];
    setSelectedFormalitiesIds: React.Dispatch<React.SetStateAction<number[]>>;
    getItemActions: (
        formality: DossierFormaliteProcessListRowProps,
    ) => Array<ContextualMenuItemProps>;
    process?: ListRowProcess;
    warningReason?: string;
};
const DossierFormaliteProcessListRows: React.FunctionComponent<DossierFormaliteProcessListRowsProps> =
    function (props) {
        const {
            formalities,
            selectedFormalitiesIds,
            setSelectedFormalitiesIds,
            getItemActions,
            process,
            warningReason,
        } = props;
        const { palette } = useTheme();
        const { getFormalityById } = useFormalitiesCtx();
        const { modalActions } = useModal();
        const { dossier, siblings } = useCurrentDossierCtx();
        const { deleteFormality, canDeleteFormality } = useDossierProcesses();
        const { actionDeleteFormality } = useDossierProcessesActionsAndRender();
        const dispatch = useCurrentDossierCtxDispatch();
        const { refreshDossierDocumentsContext } = useDossierDocumentsCtx();

        const refreshDossier = useCallback(() => {
            if (dispatch) {
                dispatch({ type: REFRESH_CURRENT_DOSSIER_CTX });
                refreshDossierDocumentsContext(
                    siblings?.filter((s) => s.id != null).map((s) => s.id as number) ?? [],
                );
            }
        }, [dispatch, refreshDossierDocumentsContext, siblings]);

        const handleCheckRow = (
            e: React.ChangeEvent<HTMLInputElement>,
            formalite: DossierFormaliteProcessListRowProps,
        ) => {
            if (e.target.checked) {
                setSelectedFormalitiesIds((ids) => [...ids, formalite.id]);
            } else {
                setSelectedFormalitiesIds(
                    selectedFormalitiesIds.filter((id) => id !== formalite.id),
                );
            }
        };

        const iconWrapperSx = {
            display: 'flex',
            alignItems: 'center',
            columnGap: '4px',
        };

        const documentCountLabelSx = {
            fontWeight: 500,
            fontSize: '13px',
            lineHeight: 1.8,
            color: lighten(palette.secondary.main, 0.3),
        };

        const iconSx = {
            fontSize: '12px',
            lineHeight: 1,
            color: lighten(palette.secondary.main, 0.3),
        };

        const validatorSx = {
            whiteSpace: 'nowrap',
            fontWeight: 500,
            fontSize: '13px',
            lineHeight: 1.8,
            color: lighten(palette.secondary.main, 0.3),
            cursor: process?.onValidatorClick ? 'pointer' : 'static',
            '&:hover': {
                color: lighten(palette.secondary.main, process?.onValidatorClick ? 0 : 0.3),
            },
        };

        const handleOnUpdateGreffe = (greffesFormalitie: number[], prestaId: number[]) => {
            modalActions.call(
                <SelectGreffeModal
                    greffeChoices={greffesFormalitie}
                    onClose={() => {
                        modalActions.close();
                    }}
                    prestaId={prestaId}
                />,
            );
        };

        const getValidatorCanal = (processItem?: ListRowProcess) => {
            if (!processItem) {
                return 'Aucun';
            }

            if (!processItem.identifier) {
                return processItem.validatorLabel;
            }

            return `${processItem.validatorLabel} (${processItem.identifier})`;
        };

        return (
            <Table sx={{ width: '100%' }}>
                {formalities.map((formalite, index) => {
                    const refFormalite = getFormalityById(formalite.id);
                    const formalityValidator = (refFormalite?.validators || []).find(
                        (v) => v.validator === process?.validatorId,
                    );
                    const hasGreffes = (formalite.greffes?.length ?? 0) > 0;
                    const greffesFormalitie = (formalite.greffes ?? [])
                        .map((g) => g.id)
                        .filter((id): id is number => id !== undefined);

                    const formaliteActions: Array<ContextualMenuItemProps> = [
                        ...getItemActions(formalite),
                        {
                            label: 'Modifier les greffes',
                            icon: 'scale-balanced' as IconName,
                            onClick: () => {
                                handleOnUpdateGreffe(greffesFormalitie, formalite.prestationId);
                            },
                        },
                    ];

                    if (dossier && canDeleteFormality(dossier)) {
                        formaliteActions.push(
                            actionDeleteFormality(
                                async () => {
                                    modalActions.close();

                                    try {
                                        await deleteFormality(formalite.id, dossier);
                                        enqueueSnackbar('La démarche a bien été supprimée');
                                        refreshDossier();
                                    } catch (e) {
                                        console.error(e);
                                        enqueueSnackbar(
                                            'Une erreur est survenue lors de la suppression de la démarche',
                                            {
                                                variant: 'error',
                                            },
                                        );
                                    }
                                },
                                () => modalActions.close(),
                            ),
                        );
                    }

                    return (
                        <InformationTableRow
                            key={formalite.id}
                            cellsSx={[
                                {
                                    width: '5%',
                                    display: 'table-cell',
                                },
                                { width: '5%' },
                                { width: '30%' },
                                { width: '25%' },
                                {
                                    width: process ? '10%' : '15%',
                                    maxWidth: 0,
                                    overflow: 'hidden',
                                },
                                { width: process ? '10%' : '15%' },
                                { width: process ? '10%' : '0%' },
                                { width: '5%' },
                            ]}
                            cels={[
                                <Checkbox
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`'row'-${index}`}
                                    sx={{
                                        transition: 'opacity ease-in-out 300ms',
                                        width: '100%',
                                    }}
                                    checked={!!selectedFormalitiesIds.includes(formalite.id)}
                                    onChange={(e) => handleCheckRow(e, formalite)}
                                />,
                                index + 1,
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        columnGap: '0.5em',
                                    }}
                                >
                                    {warningReason && (
                                        <TextIcon
                                            gap="8px"
                                            icon={alertErrorIcon}
                                            iconTooltip={warningReason}
                                        >
                                            {formalite.libelle}
                                        </TextIcon>
                                    )}
                                    {!warningReason && formalite.libelle}
                                </Box>,
                                <Box
                                    sx={{ display: 'flex', alignItems: 'center', columnGap: '8px' }}
                                >
                                    {formalite.documentsCount > 0 && (
                                        <Box sx={iconWrapperSx}>
                                            <FaOptionIcon
                                                icon={['fas', 'paperclip']}
                                                sx={iconSx}
                                            />
                                            <Typography
                                                variant="l2dcaption"
                                                sx={documentCountLabelSx}
                                            >
                                                {formalite.documentsCount}
                                            </Typography>
                                        </Box>
                                    )}
                                    {formalite.optionsCount > 0 && (
                                        <Box sx={iconWrapperSx}>
                                            <FaOptionIcon
                                                icon={['fas', 'gear']}
                                                sx={iconSx}
                                            />
                                            <Typography
                                                variant="l2dcaption"
                                                sx={documentCountLabelSx}
                                            >
                                                {formalite.optionsCount}
                                            </Typography>
                                        </Box>
                                    )}
                                    {formalite.complete && (
                                        <Box sx={iconWrapperSx}>
                                            <FaOptionIcon
                                                icon={['fas', 'pen-field']}
                                                sx={iconSx}
                                            />
                                        </Box>
                                    )}
                                    {formalite.externalUrl && (
                                        <Box sx={iconWrapperSx}>
                                            <Tooltip title={formalite.externalUrl}>
                                                <Button
                                                    variant="text"
                                                    onClick={() =>
                                                        formalite?.onExternalUrlClick(
                                                            formalite.externalUrl as unknown as string,
                                                        )
                                                    }
                                                    sx={{
                                                        minWidth: 'auto',
                                                        width: 'auto',
                                                        padding: '0',
                                                    }}
                                                >
                                                    <FaOptionIcon
                                                        icon={['fas', 'link']}
                                                        sx={iconSx}
                                                    />
                                                </Button>
                                            </Tooltip>
                                        </Box>
                                    )}
                                    <Box sx={iconWrapperSx}>
                                        <Tooltip
                                            title={
                                                formalityValidator?.documents
                                                    ? 'Cette formalité est configurée pour communiquer les documents'
                                                    : 'Cette formalité est configurée pour ne pas communiquer les documents'
                                            }
                                        >
                                            <Box>
                                                <FaOptionIcon
                                                    icon={[
                                                        'fas',
                                                        formalityValidator?.documents
                                                            ? 'file-circle-check'
                                                            : 'file-circle-exclamation',
                                                    ]}
                                                    sx={
                                                        formalityValidator?.documents
                                                            ? {
                                                                  ...iconSx,
                                                                  color: palette.success.main,
                                                              }
                                                            : {
                                                                  ...iconSx,
                                                                  color: palette.grey[400],
                                                              }
                                                    }
                                                />
                                            </Box>
                                        </Tooltip>
                                    </Box>
                                    <Box sx={iconWrapperSx}>
                                        <Tooltip
                                            title={
                                                formalityValidator?.forms
                                                    ? 'Cette formalité est configurée pour communiquer les informations des formulaires'
                                                    : 'Cette formalité est configurée pour ne pas communiquer les informations des formulaires'
                                            }
                                        >
                                            <Box>
                                                <FaOptionIcon
                                                    icon={[
                                                        'fas',
                                                        formalityValidator?.forms
                                                            ? 'memo-circle-check'
                                                            : 'memo-circle-info',
                                                    ]}
                                                    sx={
                                                        formalityValidator?.forms
                                                            ? {
                                                                  ...iconSx,
                                                                  color: palette.success.main,
                                                              }
                                                            : {
                                                                  ...iconSx,
                                                                  color: palette.grey[400],
                                                              }
                                                    }
                                                />
                                            </Box>
                                        </Tooltip>
                                    </Box>
                                </Box>,
                                <Box
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'block',
                                        width: '100%',
                                    }}
                                >
                                    <Tooltip
                                        title={
                                            hasGreffes
                                                ? formalite
                                                      .greffes!.map((g) => g.libelle)
                                                      .join(' / ')
                                                : ''
                                        }
                                    >
                                        <Typography
                                            variant="l2dcaption"
                                            onClick={
                                                !hasGreffes
                                                    ? () =>
                                                          handleOnUpdateGreffe(
                                                              greffesFormalitie,
                                                              formalite.prestationId,
                                                          )
                                                    : undefined
                                            }
                                            sx={!hasGreffes ? { cursor: 'pointer' } : undefined}
                                        >
                                            {hasGreffes
                                                ? formalite
                                                      .greffes!.map((g) => g.libelle)
                                                      .join(' / ')
                                                : 'Aucun'}
                                        </Typography>
                                    </Tooltip>
                                </Box>,
                                <Typography
                                    variant="l2dcaption"
                                    sx={validatorSx}
                                    onClick={process?.onValidatorClick}
                                >
                                    {getValidatorCanal(process)}
                                </Typography>,
                                <Box sx={iconWrapperSx}>
                                    {process ? (
                                        <DossierArticleProcessStatus
                                            statusStyle={process?.statusStyle}
                                            statusLabel={process?.statusLabel}
                                            onClick={process.onStatusClick}
                                        />
                                    ) : null}
                                </Box>,
                                <ContextualMenu
                                    items={formaliteActions}
                                    buttonSx={{ padding: 0 }}
                                />,
                            ]}
                        />
                    );
                })}
            </Table>
        );
    };

export default DossierFormaliteProcessListRows;
